/**
 * Les nombres qui giclent (livré en 0.26.0, E§1.2).
 *
 * « +12,4 k » jaillit du bâtiment à chaque encaissement visible, monte, s'évanouit —
 * taille proportionnelle au LOGARITHME du montant : sur les trente ordres de grandeur
 * d'un idle, c'est la seule échelle qui reste lisible.
 *
 * Trois décisions, héritées du vol des pièces (`coinFlight.ts`) :
 *
 * **Espace écran, pas espace monde.** Le nombre naît au-dessus d'un bâtiment mais ne
 * doit pas dériver si la caméra bouge pendant sa montée : la position est convertie une
 * fois, à l'émission.
 *
 * **Réserve d'objets, jamais d'allocation dans la boucle** (E§3). Les `Text` sont
 * recyclés ; changer `.text` re-rastérise une petite chaîne, ce qui est indolore au
 * débit plafonné — le plafond de `CoinFlight` (12/s) s'applique de fait, puisque c'est
 * lui qui appelle.
 *
 * **Le texte est fourni par l'appelant.** Le rendu ne connaît ni la langue ni le
 * format compact (« 12,4 k » en FR, « 12.4k » en EN) : `format` vient du câblage,
 * comme la cible du vol des pièces. `src/render` reste sans dépendance i18n.
 */

import { Text, type Container } from 'pixi.js';

import { JUICE } from '@/config/juice';
import type { TweenRunner } from '@/core/tween';
import { Pool } from './pool';
import type { ScreenPoint } from './coinFlight';

export interface FloatingNumbersOptions {
  /** Calque parent, en espace écran — le même que les pièces. */
  readonly layer: Container;
  readonly tweens: TweenRunner;
  /** Montant → texte affiché (localisation comprise). */
  readonly format: (value: number) => string;
}

/** Dernier nombre émis — lu par les sondes (`?debug`), jamais par le jeu. */
export interface LastLabel {
  readonly text: string;
  readonly fontPx: number;
}

/** Taille de police pour un montant donné. Exportée pour être testée sans rendu. */
export function gainFontPx(value: number): number {
  const { baseFontPx, growthFontPx, maxFontPx } = JUICE.gainLabel;
  const magnitude = Math.log10(Math.max(1, value));
  return Math.min(maxFontPx, baseFontPx + growthFontPx * magnitude);
}

export class FloatingNumbers {
  private readonly pool: Pool<Text>;

  /** Compteur d'émissions, aussi utilisé pour alterner la dérive gauche/droite. */
  private emitted = 0;

  private last: LastLabel | null = null;

  constructor(private readonly options: FloatingNumbersOptions) {
    this.pool = new Pool<Text>({
      create: () => {
        const label = new Text({
          text: '',
          style: {
            // La police du HUD (chargée par la page) : le nombre appartient à
            // l'interface, pas au décor — c'est le même chiffre que le compteur.
            fontFamily: 'Nunito, system-ui, sans-serif',
            fontWeight: '800',
            fontSize: JUICE.gainLabel.baseFontPx,
            fill: 0xffe9a8,
            stroke: { color: 0x5c3d10, width: 3 },
          },
        });
        label.anchor.set(0.5, 1);
        label.visible = false;
        this.options.layer.addChild(label);
        return label;
      },
      reset: (label) => {
        label.visible = false;
        label.alpha = 1;
        label.scale.set(1);
      },
      // Le débit est déjà plafonné en amont (12/s pour 900 ms de vie ≈ 11 vivants) ;
      // la réserve borne le pire cas sans jamais le laisser coûter des fps.
      initialSize: 4,
      maxSize: 16,
    });
  }

  /** Fait jaillir un montant depuis un point écran. Silencieux si la réserve est vide. */
  emit(from: ScreenPoint, value: number, now: number): void {
    const label = this.pool.acquire();
    if (label === null) return;

    const fontPx = gainFontPx(value);
    label.text = this.options.format(value);
    label.style.fontSize = fontPx;
    this.last = { text: label.text, fontPx };

    // Dérive alternée gauche/droite, amplitude tirée : deux gains successifs du même
    // bâtiment ne se recouvrent pas, sans jamais partir en biais permanent.
    const side = this.emitted % 2 === 0 ? 1 : -1;
    this.emitted += 1;
    const drift = side * (0.35 + Math.random() * 0.65) * JUICE.gainLabel.driftPx;

    label.visible = true;
    label.position.set(from.x, from.y);
    label.zIndex = 11; // Au-dessus des pièces : le nombre est l'information, la pièce l'ornement.

    this.options.tweens.add(
      {
        from: 0,
        to: 1,
        durationMs: JUICE.gainLabel.floatMs,
        easing: 'out',
        onUpdate: (t) => {
          label.position.set(from.x + drift * t, from.y - JUICE.gainLabel.risePx * t);
          // Pleine présence les deux premiers tiers, évanouissement ensuite : un texte
          // qui pâlit dès sa naissance ne se lit jamais.
          label.alpha = t < 0.66 ? 1 : 1 - (t - 0.66) / 0.34;
        },
        onComplete: () => {
          this.pool.release(label);
        },
      },
      now,
    );
  }

  /** Nombres actuellement à l'écran — lu par les sondes et les tests. */
  get active(): number {
    return this.pool.inUse;
  }

  /** Dernier nombre émis (texte et taille) — réservé aux sondes `?debug`. */
  get lastLabel(): LastLabel | null {
    return this.last;
  }
}
